import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const EnvSchema = z.enum(["sandbox", "live"]);

export const resolvePaddlePrice = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ priceId: z.string().min(1).max(120), environment: EnvSchema }).parse(data),
  )
  .handler(async ({ data }) => {
    const { gatewayFetch } = await import("@/lib/paddle.server");
    const res = await gatewayFetch(
      data.environment,
      `/prices?external_id=${encodeURIComponent(data.priceId)}`,
    );
    const json = (await res.json()) as { data?: Array<{ id: string }> };
    if (!json.data?.length) throw new Error(`Price not found: ${data.priceId}`);
    return json.data[0]!.id;
  });

export const getMySubscription = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ environment: EnvSchema }).parse(d))
  .handler(async ({ data, context }) => {
    const { data: row } = await context.supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", context.userId)
      .eq("environment", data.environment)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    return row;
  });

export const getMyUsage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const monthStart = new Date();
    monthStart.setUTCDate(1);
    monthStart.setUTCHours(0, 0, 0, 0);
    const period = monthStart.toISOString().slice(0, 10);
    const { data: row } = await context.supabase
      .from("monthly_usage")
      .select("*")
      .eq("user_id", context.userId)
      .eq("period_month", period)
      .maybeSingle();
    return row ?? { scans_used: 0, ai_queries_used: 0, dossiers_used: 0, period_month: period };
  });

export const getCustomerPortalUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ environment: EnvSchema }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: sub } = await supabaseAdmin
      .from("subscriptions")
      .select("paddle_customer_id, paddle_subscription_id, environment")
      .eq("user_id", context.userId)
      .eq("environment", data.environment)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!sub) throw new Error("No subscription found");
    const { getPaddleClient } = await import("@/lib/paddle.server");
    const paddle = getPaddleClient(data.environment);
    const session = await paddle.customerPortalSessions.create(sub.paddle_customer_id, [
      sub.paddle_subscription_id,
    ]);
    return { url: session.urls.general.overview };
  });

/**
 * Cancel the caller's subscription at the payment provider and immediately
 * revoke platform access. We cancel "immediately" (not at period end) so the
 * provider and the platform can never disagree about entitlement state.
 */
export const cancelMySubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ environment: EnvSchema }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const db = supabaseAdmin as any;
    const { data: sub } = await db
      .from("subscriptions")
      .select("id, paddle_subscription_id, status")
      .eq("user_id", context.userId)
      .eq("environment", data.environment)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!sub) throw new Error("No subscription found");
    if (sub.status === "canceled") return { ok: true as const, alreadyCanceled: true };

    const { gatewayFetch } = await import("@/lib/paddle.server");
    const res = await gatewayFetch(data.environment, `/subscriptions/${sub.paddle_subscription_id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ effective_from: "immediately" }),
    });
    if (!res.ok && res.status !== 404) {
      const body = await res.text().catch(() => "");
      throw new Error(`Cancellation failed at the payment provider (${res.status}). ${body.slice(0, 200)}`);
    }

    const nowIso = new Date().toISOString();
    const { error } = await db
      .from("subscriptions")
      .update({
        status: "canceled",
        cancel_at_period_end: false,
        current_period_end: nowIso,
        updated_at: nowIso,
      })
      .eq("id", sub.id);
    if (error) throw new Error(error.message);

    const { recordOperationsEvent } = await import("@/lib/operations/telemetry.server");
    await recordOperationsEvent({
      category: "billing",
      operation: "cancel_subscription",
      outcome: "succeeded",
      userId: context.userId,
      metadata: { environment: data.environment },
    });

    return { ok: true as const, alreadyCanceled: false };
  });